"use client";
// @ts-strict-ignore
import { clsx } from "clsx";
import * as React from "react";
import { usePathname } from "i18n/routing";
import { useLocale } from "next-intl";
import { getMainNavigationLinks } from "../main-nav/navigation-links";
import NextLink from "components/parts/NextLink";
import { MobileSubMenu } from "./subnav/MobileSubMenu";
import { HTMLButton } from "components/parts/button";
import { ButtonContainer, MobileNavContainer, NonExpandingMenu } from "./style";
import darkTheme from "themes/dark";
import { useBodyLock } from "utils/hooks";
import { Heading, ResponsiveBody } from "components/parts/Typography";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { Col } from "components/layout/Grid";
import Accordion from "components/parts/Accordion";
import Image from "next/image";
import Hamburger from "public/static/images/dark-theme/hamburger.svg";
import CloseMenu from "public/static/images/dark-theme/close-menu.png";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";
import { Caret } from "../Caret";
import { usePathnameChecks } from "hooks/usePathnameChecks";
import { NAV_LINK_KEYS } from "../main-nav/constants";

type MobileNavProps = {
  navItems?: React.ReactNode;
  bottomItems?: React.ReactNode;
};

export const MobileNav = ({ navItems, bottomItems }: MobileNavProps) => {
  const pathname = usePathname();
  const locale = useLocale();
  const { country } = useCountryStateCookies();
  const { isGoogleSearchPath } = usePathnameChecks();

  const [isOpened, setIsOpened] = React.useState(false);
  useBodyLock(isOpened);
  React.useEffect(() => {
    setIsOpened(false);
  }, [pathname]);

  const t = useTranslations(FILES.COMMON);

  const menuChoices = {
    [NAV_LINK_KEYS.CreditCard]: (
      <MobileSubMenu menuType={NAV_LINK_KEYS.CreditCard} />
    ),
    [NAV_LINK_KEYS.Products]: (
      <MobileSubMenu menuType={NAV_LINK_KEYS.Products} />
    ),
    [NAV_LINK_KEYS.Resources]: (
      <MobileSubMenu menuType={NAV_LINK_KEYS.Resources} />
    ),
    [NAV_LINK_KEYS.Institutions]: (
      <MobileSubMenu menuType={NAV_LINK_KEYS.Institutions} />
    )
  };

  // Memoize base navigation links to ensure stable reference
  const navigationLinks = React.useMemo(
    () =>
      getMainNavigationLinks({
        t,
        locale,
        countryCode: country
      }),
    [t, locale, country]
  );

  const renderMainNavigation = () => {
    return navigationLinks.map((navItem) => {
      const key = `MenuItem-${navItem.key}`;

      if (navItem.hasMenu) {
        return (
          <Accordion initialOpenedIndex={0} key={key}>
            [
            <Accordion.Collapse
              customTitle={(isOpen) => {
                return (
                  <Col
                    sm={12}
                    alignItems="center"
                    style={{ padding: "32px 0 0 0" }}
                  >
                    <Heading size="xl" color="white" css={{ margin: 0 }}>
                      {navItem.name}
                    </Heading>
                    <Caret itemIsOpen={isOpen} />
                  </Col>
                );
              }}
              css={`
                div {
                  padding: 0;
                }
              `}
              isMobileNav={true}
            >
              <Col>{menuChoices[navItem.key]}</Col>
            </Accordion.Collapse>
            ]
          </Accordion>
        );
      }

      return (
        <NonExpandingMenu key={key}>
          <ResponsiveBody semiBold as={"h6"}>
            <Col sm={12} alignItems="center" style={{ padding: "0" }}>
              <Heading
                size="xl"
                color="white"
                css={{ margin: 0 }}
                onClick={() => setIsOpened(false)}
              >
                <NextLink href={navItem.url} prefetch={false}>
                  {navItem.name}
                </NextLink>
              </Heading>
            </Col>
          </ResponsiveBody>
        </NonExpandingMenu>
      );
    });
  };

  const renderOpeners = () => {
    if (isGoogleSearchPath) return null;

    if (isOpened) {
      return (
        <HTMLButton
          onClick={(e) => {
            e.preventDefault();
            setIsOpened(false);
          }}
        >
          <Image src={CloseMenu} alt="Close icon" width={25} height={25} />
        </HTMLButton>
      );
    }

    return (
      <HTMLButton
        onClick={(e) => {
          e.preventDefault();
          setIsOpened(true);
        }}
      >
        <Image src={Hamburger} alt="Open" width={25} height={25} />
      </HTMLButton>
    );
  };

  const renderMobileNavHeader = () => (
    <MobileNavContainer theme={darkTheme}>
      <NextLink raw href="/" style={{ display: "contents" }}>
        <Image
          src={
            isGoogleSearchPath
              ? "/static/images/gemini-hor-light-full-white-rgb@3x.png"
              : "/static/images/gemini-hor-light-full-rgb@3x.png"
          }
          alt="Gemini Logo"
          height={20}
          width={92}
        />
      </NextLink>
      {!isOpened ? (
        <ButtonContainer>
          {navItems}
          {renderOpeners()}
        </ButtonContainer>
      ) : (
        <ButtonContainer>{renderOpeners()}</ButtonContainer>
      )}
    </MobileNavContainer>
  );

  return (
    <div
      className={clsx(
        "pointer-events-none sticky inset-0 z-[100] flex w-full flex-col",
        isOpened && "h-[100dvh]",
        "desktop:hidden"
      )}
    >
      {renderMobileNavHeader()}
      <div
        className={clsx(
          "z-[100] box-border flex flex-grow flex-col",
          "bg-black",
          "transition-opacity duration-300 ease-in-out",
          isOpened
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-full opacity-0"
        )}
      >
        <div className="relative flex flex-grow flex-col">
          <div className="absolute inset-0 flex flex-col overflow-x-hidden overflow-y-auto pl-[30px]">
            {renderMainNavigation()}
          </div>
        </div>

        <div className="z-[5000] mt-auto flex w-[90%] flex-col gap-2 self-center bg-black pb-[30px]">
          {bottomItems}
        </div>
      </div>
    </div>
  );
};

export default MobileNav;
